import seedrandom from 'seedrandom'
import { TableOfContentsItem } from '@stoplight/elements-core'

import { NodeType } from '@stoplight/types'
import { OperationNode, ServiceNode } from '../types'

type TagGroup = { title: string; items: OperationNode[] }

const generateRandom = seedrandom('d7dce89a-85e1-4117-9dd4-ac00428c7161')

export const generateRandomInteger = (min: number, max: number) => {
  if (min >= max) {
    throw new Error('The value for max must be greater than min')
  }
  const rand = generateRandom()
  return Math.round(min + -rand * min + rand * max)
}

export const findFirstNodeSlug = (
  tree: TableOfContentsItem[]
): string | void => {
  for (const item of tree) {
    if ('slug' in item) {
      return item.slug
    }

    if ('items' in item) {
      const slug = findFirstNodeSlug(item.items)
      if (slug) {
        return slug
      }
    }
  }
}

export const computeAPITree = (
  serviceNode: ServiceNode,
  basePath: string = ''
) => {
  const tree: TableOfContentsItem[] = []

  tree.push(
    {
      id: basePath,
      slug: basePath,
      title: 'Overview',
      type: 'overview',
      meta: '',
    },
    {
      id: `${basePath}/articles/devices/device-filters`,
      slug: `${basePath}/articles/devices/device-filters`,
      title: 'Device Filters',
      type: 'article',
      meta: '',
    }
  )

  const operationNodes = serviceNode.children.filter(
    (node) => node.type === NodeType.HttpOperation
  )

  if (operationNodes.length) {
    const { groups, ungrouped } = computeTagGroups(serviceNode)

    // Show ungrouped operations above tag groups
    ungrouped.forEach((operationNode) => {
      tree.push({
        id: `${basePath}${operationNode.uri}`,
        slug: `${basePath}${operationNode.uri}`,
        title: operationNode.name,
        type: operationNode.type,
        meta: operationNode.data.method,
      })
    })

    groups.forEach((group) => {
      if (group.title?.toLowerCase() === 'webhooks') {
        // TODO: create util function to add custom article
        group.items.unshift({
          uri: '/articles/webhooks/overview',
          name: 'Overview',
          // @ts-expect-error
          type: 'article',
        })
      }

      tree.push({
        title: group.title,
        items: group.items.flatMap((operationNode) => {
          return {
            id: `${basePath}${operationNode.uri}`,
            slug: `${basePath}${operationNode.uri}`,
            title: operationNode.name,
            type: operationNode.type,
            meta: operationNode.data?.method,
          }
        }),
      })
    })
  }

  return tree
}

const computeTagGroups = (serviceNode: ServiceNode) => {
  const groupsByTagId: { [tagId: string]: TagGroup } = {}
  const ungrouped = []

  const lowerCaseServiceTags = serviceNode.tags.map((tn) => tn.toLowerCase())

  for (const node of serviceNode.children) {
    if (node.type !== NodeType.HttpOperation) continue
    const tagName = node.tags[0]

    if (tagName) {
      const tagId = tagName.toLowerCase()
      if (groupsByTagId[tagId]) {
        groupsByTagId[tagId].items.push(node)
      } else {
        const serviceTagName = lowerCaseServiceTags.find((tn) => tn === tagId)

        groupsByTagId[tagId] = {
          title: serviceTagName || tagName,
          items: [node],
        }
      }
    } else {
      ungrouped.push(node)
    }
  }

  const orderedTagGroups = Object.entries(groupsByTagId)
    .sort(([g1], [g2]) => {
      const g1LC = g1.toLowerCase()
      const g2LC = g2.toLowerCase()
      const g1Idx = lowerCaseServiceTags.findIndex((tn) => tn === g1LC)
      const g2Idx = lowerCaseServiceTags.findIndex((tn) => tn === g2LC)

      // Move not-tagged groups to the bottom
      if (g1Idx < 0 && g2Idx < 0) return 0
      if (g1Idx < 0) return 1
      if (g2Idx < 0) return -1

      // sort tagged groups according to the order found in HttpService
      return g1Idx - g2Idx
    })
    .map(([, tagGroup]) => tagGroup)

  return { groups: orderedTagGroups, ungrouped }
}
